import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiPhone, FiBriefcase, FiGlobe } from 'react-icons/fi';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { leadsApi } from '../../utils/api';
import './AuthPages.css';

const RegisterPage = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if type=brand from URL parameter
  const typeParam = searchParams.get('type');
  const defaultRole = typeParam === 'brand' ? 'brand' : (searchParams.get('role') || 'creator');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      role: defaultRole
    }
  });

  const selectedRole = watch('role');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (data.role === 'brand') {
        // Brand registration - create lead (not user account)
        const leadData = {
          fullName: data.fullName,
          phone: data.phone,
          email: data.email,
          companyName: data.companyName,
          website: data.website,
          hadInfluencerActivity: data.hadInfluencerActivity === 'yes',
          monthlyBudget: data.monthlyBudget,
          source: 'brand_registration'
        };
        await leadsApi.create(leadData);

        // Show SweetAlert success message
        await Swal.fire({
          icon: 'success',
          title: 'תודה על ההרשמה!',
          text: 'הפרטים התקבלו בהצלחה. ניצור איתך קשר בהקדם.',
          confirmButtonText: 'אישור',
          confirmButtonColor: '#F7941D'
        });

        // Redirect to home page
        navigate('/');
      } else {
        // Creator registration - store locally, don't create DB record yet
        const pendingData = {
          email: data.email,
          password: data.password,
          fullName: `${data.firstName} ${data.lastName}`.trim(),
          displayName: data.firstName
        };
        localStorage.setItem('pendingCreatorRegistration', JSON.stringify(pendingData));
        navigate('/register/steps');
      }
    } catch (error) {
      toast.error(error.message || 'שגיאה בהרשמה');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left Side - Form */}
        <motion.div
          className="auth-form-section"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="auth-form-content">
            <Link to="/" className="logo">
              <span className="logo-ai">AI</span>
              <span className="logo-weon">weOn</span>
            </Link>

            <h1>צור חשבון חדש</h1>
            <p className="auth-subtitle">להצטרף לאלפי יוצרי תוכן שלנו.<br />בכל מקום שיהיה משפיען/נית</p>

            <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
              {/* Role Selection */}
              <div className="role-selection">
                <label
                  className={`role-option ${selectedRole === 'creator' ? 'active' : ''}`}
                >
                  <input
                    type="radio"
                    value="creator"
                    {...register('role')}
                  />
                  <span className="role-content role-content-center">
                    <strong>אני משפיען</strong>
                  </span>
                </label>
                <label
                  className={`role-option ${selectedRole === 'brand' ? 'active' : ''}`}
                >
                  <input
                    type="radio"
                    value="brand"
                    {...register('role')}
                  />
                  <span className="role-content role-content-center">
                    <strong>אני מותג</strong>
                  </span>
                </label>
              </div>

              {/* Brand Form */}
              {selectedRole === 'brand' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">שם מלא*</label>
                    <input
                      type="text"
                      className={`form-input ${errors.fullName ? 'error' : ''}`}
                      placeholder=""
                      {...register('fullName', {
                        required: 'שם מלא הוא שדה חובה'
                      })}
                    />
                    {errors.fullName && (
                      <span className="form-error">{errors.fullName.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">טלפון*</label>
                    <input
                      type="tel"
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder=""
                      {...register('phone', {
                        required: 'טלפון הוא שדה חובה'
                      })}
                    />
                    {errors.phone && (
                      <span className="form-error">{errors.phone.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">דוא"ל*</label>
                    <input
                      type="email"
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      placeholder=""
                      {...register('email', {
                        required: 'דוא"ל הוא שדה חובה',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'כתובת דוא"ל לא תקינה'
                        }
                      })}
                    />
                    {errors.email && (
                      <span className="form-error">{errors.email.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">שם החברה</label>
                    <input
                      type="text"
                      className={`form-input ${errors.companyName ? 'error' : ''}`}
                      placeholder=""
                      {...register('companyName')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">כתובת אתר</label>
                    <input
                      type="url"
                      className={`form-input ${errors.website ? 'error' : ''}`}
                      placeholder=""
                      {...register('website')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">האם קיימתם פעילות משפיענים בעבר?</label>
                    <div className="radio-group-horizontal">
                      <label className="radio-option">
                        <input
                          type="radio"
                          value="yes"
                          {...register('hadInfluencerActivity')}
                        />
                        <span className="radio-circle"></span>
                        <span>כן</span>
                      </label>
                      <label className="radio-option">
                        <input
                          type="radio"
                          value="no"
                          {...register('hadInfluencerActivity')}
                        />
                        <span className="radio-circle"></span>
                        <span>לא</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">מהו התקציב החודשי שברצונכם להשקיע במהלך?</label>
                    <select
                      className={`form-select ${errors.monthlyBudget ? 'error' : ''}`}
                      {...register('monthlyBudget')}
                    >
                      <option value="">בחר תקציב...</option>
                      <option value="5k-10k">5,000 - 10,000 ₪</option>
                      <option value="over10k">מעל 10,000 ₪</option>
                    </select>
                  </div>

                  <label className="checkbox-label terms-checkbox">
                    <input
                      type="checkbox"
                      {...register('marketingConsent')}
                    />
                    <span>מאשר/ת קבלת מידע ותוכן שיווקי</span>
                  </label>
                </>
              ) : (
                <>
                  {/* Creator Form - Original fields */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">שם פרטי</label>
                      <div className="input-with-icon">
                        <FiUser className="input-icon" />
                        <input
                          type="text"
                          className={`form-input ${errors.firstName ? 'error' : ''}`}
                          placeholder="שם פרטי"
                          {...register('firstName', {
                            required: 'שם פרטי הוא שדה חובה'
                          })}
                        />
                      </div>
                      {errors.firstName && (
                        <span className="form-error">{errors.firstName.message}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">שם משפחה</label>
                      <div className="input-with-icon">
                        <FiUser className="input-icon" />
                        <input
                          type="text"
                          className={`form-input ${errors.lastName ? 'error' : ''}`}
                          placeholder="שם משפחה"
                          {...register('lastName', {
                            required: 'שם משפחה הוא שדה חובה'
                          })}
                        />
                      </div>
                      {errors.lastName && (
                        <span className="form-error">{errors.lastName.message}</span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">אימייל</label>
                    <div className="input-with-icon">
                      <FiMail className="input-icon" />
                      <input
                        type="email"
                        className={`form-input ${errors.email ? 'error' : ''}`}
                        placeholder="your@email.com"
                        {...register('email', {
                          required: 'אימייל הוא שדה חובה',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'כתובת אימייל לא תקינה'
                          }
                        })}
                      />
                    </div>
                    {errors.email && (
                      <span className="form-error">{errors.email.message}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">סיסמה</label>
                    <div className="input-with-icon">
                      <FiLock className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-input ${errors.password ? 'error' : ''}`}
                        placeholder="בחר סיסמה (6 תווים לפחות)"
                        {...register('password', {
                          required: 'סיסמה היא שדה חובה',
                          minLength: {
                            value: 6,
                            message: 'סיסמה חייבת להכיל לפחות 6 תווים'
                          }
                        })}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {errors.password && (
                      <span className="form-error">{errors.password.message}</span>
                    )}
                  </div>

                  <label className="checkbox-label terms-checkbox">
                    <input
                      type="checkbox"
                      {...register('terms', {
                        required: 'יש לאשר את תנאי השימוש'
                      })}
                    />
                    <span>
                      אני מסכים/ה ל<Link to="/terms" target="_blank">תנאי השימוש</Link> ול<Link to="/privacy" target="_blank">מדיניות הפרטיות</Link>
                    </span>
                  </label>
                  {errors.terms && (
                    <span className="form-error">{errors.terms.message}</span>
                  )}
                </>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isLoading}
              >
                {isLoading ? 'נרשם...' : 'הרשמה'}
              </button>
            </form>

            <p className="auth-switch">
              כבר יש לך חשבון?{' '}
              <Link to="/creator-login">התחבר</Link>
            </p>
          </div>
        </motion.div>

        {/* Right Side - Visual */}
        <motion.div
          className="auth-visual-section"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="visual-content">
            <h2>הצטרף ל-20,000+</h2>
            <h2>יוצרי תוכן</h2>
            <h2 className="text-primary">במקום אחד</h2>
            <p>
              קבל גישה לקמפיינים בלעדיים
              מהמותגים המובילים בישראל
            </p>
          </div>
          <div className="visual-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
            <div className="shape shape-4"></div>
            <div className="shape shape-5"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
